/**
 * Sweeps non-allowlisted cookies out of `document.cookie` when the user revokes
 * any non-essential OneTrust category mid-session. Cookies set before opt-out
 * (e.g. `_ga`, `_fbp`, `_ttp`) would otherwise persist and be read by third
 * parties on the next page load.
 */

import { deleteCookie as cookiesNextDelete, getCookies } from 'cookies-next';
import {
  BANNER_COOKIE_PREFIX,
  CUSTOM_QSP_COOKIE,
  EMAIL_CAPTURE_COOKIE,
  FP_VIEWED_COOKIE,
  INCENTIVE_FIELDS_COOKIE,
  JOINT_CHECKING_TOKEN_COOKIE,
  LAST_CAMPAIGN_COOKIE,
  ONETRUST_BANNER_CLOSED_COOKIE,
  ONETRUST_CONSENT_COOKIE,
  REFERRAL_TOKEN_COOKIE,
  REFERRER_NAME_COOKIE,
  REFERRER_USER_ID_COOKIE,
  SAVED_JOBS_COOKIE,
} from '@/utils/cookies';
import { getHostSuffixes } from '@/utils/url';
import { onConsentChange } from './index';
import { logConsentEvent } from './observability';

/**
 * Cookies that must survive a delete sweep.
 * `banner-{id}` cookies (set per-banner by FeaturedBanner) are matched by
 * prefix, not by exact name, since the id is dynamic.
 *
 * Format contract — do not change without updating
 * `scripts/onetrust-checks/dump-cookie-allowlist.sh`, which parses this file
 * with grep/sed:
 *   - One identifier per line inside the `new Set<string>([ ... ])` literal.
 *   - Each identifier must resolve to a `*_COOKIE` constant exported from
 *     `utils/cookies.ts`.
 */
export const COOKIE_ALLOWLIST: ReadonlySet<string> = new Set<string>([
  ONETRUST_CONSENT_COOKIE,
  ONETRUST_BANNER_CLOSED_COOKIE,
  FP_VIEWED_COOKIE,
  REFERRAL_TOKEN_COOKIE,
  REFERRER_NAME_COOKIE,
  REFERRER_USER_ID_COOKIE,
  INCENTIVE_FIELDS_COOKIE,
  LAST_CAMPAIGN_COOKIE,
  JOINT_CHECKING_TOKEN_COOKIE,
  EMAIL_CAPTURE_COOKIE,
  SAVED_JOBS_COOKIE,
  CUSTOM_QSP_COOKIE,
]);

export function isAllowlisted(name: string): boolean {
  if (COOKIE_ALLOWLIST.has(name)) return true;
  return name.startsWith(BANNER_COOKIE_PREFIX);
}

/**
 * Deletes a cookie across the (path × domain) combinations realistically used
 * by chime.com's set-sites. We brute-force the grid because the browser only
 * honors the delete when the domain/path matches the original write, and we
 * don't know which combination a given third-party script chose. Deletes
 * whose `(name, path, domain)` tuple doesn't match a real cookie are silently
 * ignored, so over-attempting is safe.
 */
export function deleteCookie(name: string): void {
  if (typeof window === 'undefined') return;

  // No-domain delete (current host, implicit).
  cookiesNextDelete(name, { path: '/' });

  for (const domain of getHostSuffixes(window.location.hostname)) {
    cookiesNextDelete(name, { path: '/', domain });
    cookiesNextDelete(name, { path: '/', domain: `.${domain}` });
  }
}

/**
 * Iterates every cookie currently readable via `document.cookie` and deletes
 * any whose name isn't on the allowlist. Returns the names that were swept
 * and the names that were kept — useful for observability and tests.
 */
/** Which cookies an opt-out sweep deleted, and which the allowlist protected. */
interface CookieSweepResult {
  swept: string[];
  kept: string[];
}

export function sweepNonAllowlistedCookies(): CookieSweepResult {
  const swept: string[] = [];
  const kept: string[] = [];

  // On the browser, `getCookies()` synchronously returns an object of
  // name→value pairs. Its TS return type also covers async server usage
  // (Promise) and missing-cookies (undefined), so guard against those.
  const cookies = getCookies();
  if (!cookies || cookies instanceof Promise) return { swept, kept };

  for (const name of Object.keys(cookies)) {
    if (isAllowlisted(name)) {
      kept.push(name);
      continue;
    }
    deleteCookie(name);
    swept.push(name);
  }

  return { swept, kept };
}

/**
 * Registers an `onConsentChange` subscriber that sweeps non-allowlisted cookies
 * whenever the user revokes consent for one or more non-essential categories.
 *
 * The first invocation (initial replay on subscribe, or first OneTrust fire)
 * establishes the baseline and does NOT delete — already-opted-out visitors
 * on page load should not trigger a sweep.
 */
export function registerOptOutCookieSweep(): () => void {
  let previousGroups: string[] | null = null;

  return onConsentChange(activeGroups => {
    if (previousGroups === null) {
      previousGroups = activeGroups.slice();
      return;
    }

    const revoked = previousGroups.filter(g => !activeGroups.includes(g));
    previousGroups = activeGroups.slice();

    if (revoked.length === 0) return;

    const { swept, kept } = sweepNonAllowlistedCookies();
    logConsentEvent({
      event: 'cookieSweep',
      activeGroups,
      revokedCategories: revoked,
      swept: swept.length,
      kept,
    });
  });
}
